import React, { useEffect, useState } from 'react';
import {
  FaGripVertical,
  FaSearch,
  FaTimes,
  FaFolderOpen,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import PlantafelService, { ProjectTimelineItem } from '../services/PlantafelService';
import { PROJECT_COLORS } from './leftPanelTypes';
import { pdebugError } from '../utils/plantafelDebug';
import styles from './PlantafelLeftPanel.module.css';

interface LeftPanelProjectsProps {
  isActive: boolean;
}

export function LeftPanelProjects({
  isActive,
}: LeftPanelProjectsProps): React.JSX.Element {
  const [projects, setProjects] = useState<ProjectTimelineItem[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const projectList = await PlantafelService.fetchAllProjects(projectSearch || undefined);
        setProjects(projectList);
      } catch (err) {
        pdebugError('Failed to load projects:', err);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [isActive, projectSearch]);

  const filteredProjects = projects.filter((p) =>
    !projectSearch ||
    p.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
    p.partner_name?.toLowerCase().includes(projectSearch.toLowerCase())
  );

  return (
    <>
      <div className={styles.searchBarContainer}>
        <div className={styles.searchBarInner}>
          <FaSearch size={10} color="#888" />
          <input
            type="text"
            placeholder="Projekt suchen..."
            value={projectSearch}
            onChange={(e) => setProjectSearch(e.target.value)}
            className={styles.searchInput}
          />
          {projectSearch && (
            <button
              onClick={() => setProjectSearch('')}
              className={styles.searchClearButton}
            >
              <FaTimes size={8} />
            </button>
          )}
        </div>
        <div className={styles.searchCount}>
          {projects.length} Projekte
        </div>
      </div>

      <div className={styles.projectsList}>
        {loadingProjects ? (
          <div className={styles.loadingState}>
            Lade Projekte...
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className={styles.emptySmall}>
            {projectSearch ? 'Keine Projekte gefunden' : 'Keine Projekte vorhanden'}
          </div>
        ) : (
          filteredProjects.map((project, index) => {
            const statusColors = PlantafelService.getStatusColor(project.status);
            const isPlanned = project.planned_start && project.planned_end;
            const projectColor = PROJECT_COLORS[index % PROJECT_COLORS.length];

            return (
              <div
                key={`project-${project.id}`}
                data-testid={`project-card-${project.id}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('projectId', project.id.toString());
                  e.dataTransfer.setData('projectName', project.name);
                  if (project.planned_start && project.planned_end) {
                    e.dataTransfer.setData('plannedDuration',
                      Math.ceil((new Date(project.planned_end).getTime() - new Date(project.planned_start).getTime()) / (1000 * 60 * 60 * 24)).toString()
                    );
                  }
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                className={styles.projectCard}
                style={{ borderLeft: `3px solid ${projectColor.bg}` }}
              >
                <div className={styles.projectCardInner}>
                  <div style={{ color: projectColor.bg, marginTop: '2px' }}>
                    <FaGripVertical size={10} />
                  </div>
                  <div className={styles.projectCardBody}>
                    <div className={styles.projectName}>
                      {project.name}
                    </div>
                    {project.partner_name && (
                      <div className={styles.projectPartner}>
                        {project.partner_name}
                      </div>
                    )}
                    <div className={styles.projectTags}>
                      {project.city && (
                        <span className={styles.projectCity}>
                          <FaMapMarkerAlt size={8} />
                          {project.city}
                        </span>
                      )}
                      <span
                        className={styles.statusBadge}
                        style={{
                          background: statusColors.bg,
                          color: statusColors.text,
                        }}
                      >
                        {project.status === 'ORDER_CONFIRMED' ? 'Bestätigt' :
                         project.status === 'IN_EXECUTION' ? 'In Arbeit' :
                         project.status === 'LEAD' ? 'Lead' :
                         project.status === 'OFFER_CREATED' ? 'Angebot' : project.status}
                      </span>
                      {isPlanned && (
                        <span className={styles.plannedBadge}>
                          &#10003; geplant
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
